import React, { useEffect, useState, useContext } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ThemeContext } from '../hooks/ThemeContext';
import { CheckCircle, XCircle, AlertCircle, Home, Mail } from 'lucide-react';

const EmailVerify = () => {
  const { theme } = useContext(ThemeContext);
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const isDark = theme === 'dark';

  useEffect(() => {
    const statusParam = searchParams.get('status');
    setStatus(statusParam || 'loading');
  }, [searchParams]);

  const getStatusContent = () => {
    switch (status) {
      case 'success':
        return {
          icon: <CheckCircle className="w-20 h-20 text-green-500" />,
          title: 'Email vérifié avec succès !',
          message: 'Votre adresse email a été vérifiée. Vous pouvez maintenant utiliser toutes les fonctionnalités de votre compte.',
          color: 'green',
          actions: (
            <>
              <Link 
                to="/espace-client" 
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center gap-2"
              >
                <Home className="w-5 h-5" />
                Accéder à mon espace
              </Link>
              <Link 
                to="/" 
                className={`px-6 py-3 rounded-lg border transition-colors font-medium flex items-center gap-2 ${
                  isDark ? 'border-white/20 hover:bg-white/10 text-white' : 'border-gray-300 hover:bg-white text-gray-800'
                }`}
              >
                Retour à l'accueil
              </Link>
            </>
          )
        };

      case 'already_verified':
        return {
          icon: <AlertCircle className="w-20 h-20 text-blue-500" />,
          title: 'Email déjà vérifié',
          message: 'Cette adresse email a déjà été vérifiée. Vous pouvez vous connecter normalement.',
          color: 'blue',
          actions: (
            <>
              <Link 
                to="/espace-client" 
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center gap-2"
              >
                <Home className="w-5 h-5" />
                Accéder à mon espace
              </Link>
              <Link 
                to="/" 
                className={`px-6 py-3 rounded-lg border transition-colors font-medium flex items-center gap-2 ${
                  isDark ? 'border-white/20 hover:bg-white/10 text-white' : 'border-gray-300 hover:bg-white text-gray-800'
                }`}
              >
                Retour à l'accueil
              </Link>
            </>
          )
        };

      case 'invalid_link':
        return {
          icon: <XCircle className="w-20 h-20 text-red-500" />,
          title: 'Lien invalide ou expiré',
          message: 'Ce lien de vérification n\'est plus valide. Il a peut-être expiré ou a déjà été utilisé.',
          color: 'red',
          actions: (
            <>
              <Link 
                to="/espace-client" 
                className="px-6 py-3 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors font-medium flex items-center gap-2"
              >
                <Mail className="w-5 h-5" />
                Renvoyer un email
              </Link>
              <Link 
                to="/" 
                className={`px-6 py-3 rounded-lg border transition-colors font-medium flex items-center gap-2 ${
                  isDark ? 'border-white/20 hover:bg-white/10 text-white' : 'border-gray-300 hover:bg-white text-gray-800'
                }`}
              >
                Retour à l'accueil
              </Link>
            </>
          )
        };

      case 'user_not_found':
        return {
          icon: <XCircle className="w-20 h-20 text-red-500" />,
          title: 'Utilisateur introuvable',
          message: 'Impossible de trouver l\'utilisateur associé à ce lien.',
          color: 'red',
          actions: (
            <>
              <Link 
                to="/login" 
                className="px-6 py-3 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors font-medium"
              >
                Se connecter
              </Link>
              <Link 
                to="/register" 
                className={`px-6 py-3 rounded-lg border transition-colors font-medium ${
                  isDark ? 'border-white/20 hover:bg-white/10 text-white' : 'border-gray-300 hover:bg-white text-gray-800'
                }`}
              >
                Créer un compte
              </Link>
            </>
          )
        };

      case 'error':
        return {
          icon: <XCircle className="w-20 h-20 text-red-500" />,
          title: 'Erreur de vérification',
          message: 'Une erreur s\'est produite lors de la vérification de votre email. Veuillez réessayer.',
          color: 'red',
          actions: (
            <>
              <Link 
                to="/espace-client" 
                className="px-6 py-3 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors font-medium flex items-center gap-2"
              >
                <Mail className="w-5 h-5" />
                Renvoyer un email
              </Link>
              <Link 
                to="/" 
                className={`px-6 py-3 rounded-lg border transition-colors font-medium flex items-center gap-2 ${
                  isDark ? 'border-white/20 hover:bg-white/10 text-white' : 'border-gray-300 hover:bg-white text-gray-800'
                }`}
              >
                Retour à l'accueil
              </Link>
            </>
          )
        };

      default:
        return {
          icon: (
            <div className={`w-20 h-20 rounded-full border-4 ${isDark ? 'border-white/20 border-t-rose-500' : 'border-gray-200 border-t-rose-600'} animate-spin`} />
          ),
          title: 'Vérification en cours...',
          message: 'Veuillez patienter pendant que nous vérifions votre adresse email.',
          color: 'gray',
          actions: null
        };
    }
  };

  const content = getStatusContent();

  return (
    <div className={`flex-1 flex flex-col items-center justify-center px-4 py-16 ${isDark ? 'bg-[#0f1114] text-white' : 'bg-slate-50 text-gray-800'}`}>
      <div className={`max-w-md w-full p-8 rounded-2xl text-center ${
        isDark ? 'bg-[#0b0b0c] border border-white/10' : 'bg-white border border-gray-200'
      } shadow-xl`}>
        {/* Icon */}
        <div className="flex justify-center mb-6">
          {content.icon}
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold mb-4">
          {content.title}
        </h1>

        {/* Message */}
        <p className={`mb-8 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          {content.message}
        </p>

        {/* Actions */}
        {content.actions && (
          <div className="flex flex-col gap-3">
            {content.actions}
          </div>
        )}
      </div>

      {/* Info supplémentaire */}
      {status === 'success' && (
        <div className={`mt-6 p-4 rounded-lg max-w-md w-full text-sm ${
          isDark ? 'bg-green-500/10 border border-green-500/20 text-green-400' : 'bg-green-50 border border-green-200 text-green-700'
        }`}>
          <p className="font-medium mb-1">✓ Vérification réussie</p>
          <p className={isDark ? 'text-green-400/80' : 'text-green-600'}>
            Toutes les fonctionnalités de votre compte sont maintenant accessibles.
          </p>
        </div>
      )}
    </div>
  );
};

export default EmailVerify;
